import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaRupeeSign,
  FaClock,
} from "react-icons/fa";
import DriverLayout from "../../layouts/DriverLayout";
import { getBookingHistory, reset } from "../../features/driver/driverSlice";
import EarningsBreakdown from "../../components/Driver/EarningsBreakdown";

const RideHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { driver, bookingHistory, loading, error } = useSelector(
    (state) => state.driver,
  );
  const [filter, setFilter] = useState("completed");
  const [expandedBookings, setExpandedBookings] = useState({});

  const toggleBookingExpand = (bookingId) => {
    setExpandedBookings((prev) => ({
      ...prev,
      [bookingId]: !prev[bookingId],
    }));
  };

  const isCurrentBookingStatus = (status) => 
    status === "inProgress" || status === "assigned" || status === "confirmed";

  useEffect(() => {
    if (driver && driver._id) {
      dispatch(getBookingHistory(driver._id));
    }

    return () => {
      dispatch(reset());
    };
  }, [dispatch, driver]);


  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(reset());
    }
  }, [error, dispatch]);



  const filteredBookings = () => {
    let filtered;

    if (filter === "all") {
      filtered = bookingHistory.filter((booking) =>
        booking.status === "completed" || booking.status === "cancelled"
      );
    } else if (filter === "current") {
      filtered = bookingHistory.filter((booking) => isCurrentBookingStatus(booking.status));
    } else {
      filtered = bookingHistory.filter((booking) => booking.status === filter);
    }

    return [...filtered].sort((a, b) => {
      const statusPriority = {
        'completed': 1,
        'cancelled': 2,
        'inProgress': 3,
        'assigned': 4,
        'confirmed': 5
      };

      const priorityA = statusPriority[a.status] || 6;
      const priorityB = statusPriority[b.status] || 6;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      const dateA = new Date(a.createdAt || a.pickupDate);
      const dateB = new Date(b.createdAt || b.pickupDate);
      return dateB - dateA;
    });
  };

  const safeRender = (value, fallback = "N/A") => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "object") {
      if (value instanceof Date) return formatDateTime(value);
      if (Array.isArray(value)) return fallback;
      if (value && typeof value === 'object' && 'createdAt' in value) {
        if (value.rating !== undefined) return String(value.rating);
        if (value.value !== undefined) return String(value.value);
        if (value.comment !== undefined) return String(value.comment);
        return fallback;
      }
      return fallback;
    }
    return String(value);
  };

  const safeGetRating = (ratingObj) => {
    if (typeof ratingObj === 'number') return ratingObj;
    if (typeof ratingObj === 'object' && ratingObj !== null) {
      return ratingObj.rating || ratingObj.value || 0;
    }
    return 0;
  };

  const safeGetComment = (ratingObj) => {
    if (typeof ratingObj === 'string') return ratingObj;
    if (typeof ratingObj === 'object' && ratingObj !== null) {
      return ratingObj.comment || ratingObj.text || '';
    }
    return '';
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "completed":
        return "bg-emerald-100 text-emerald-700 border border-emerald-200";
      case "cancelled":
        return "bg-rose-100 text-rose-700 border border-rose-200";
      case "inProgress":
        return "bg-blue-600 text-white border border-blue-700";
      case "assigned":
        return "bg-orange-500 text-white border border-orange-600";
      case "confirmed":
        return "bg-purple-500 text-white border border-purple-600";
      default:
        return "bg-slate-100 text-slate-600 border border-slate-200";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  if (loading && bookingHistory.length === 0) {
    return (
      <DriverLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-black text-slate-800">Ride History</h1>
            {bookingHistory.length > 0 && (
              <div className="flex items-center space-x-3 text-xs text-slate-450 font-bold mt-1 uppercase tracking-wider">
                <span>Active: {bookingHistory.filter(b => isCurrentBookingStatus(b.status)).length}</span>
                <span>•</span>
                <span>Completed: {bookingHistory.filter(b => b.status === "completed").length}</span>
                <span>•</span>
                <span>Total: {bookingHistory.length}</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap bg-slate-100 border border-slate-200/50 p-1 rounded-xl self-start md:self-auto">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all duration-200 ${
                filter === "all"
                  ? "bg-slate-900 text-white shadow-sm"
                  : "text-slate-500 hover:text-slate-850"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter("current")}
              className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all duration-200 ${
                filter === "current"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-500 hover:text-slate-850"
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setFilter("completed")}
              className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all duration-200 ${
                filter === "completed"
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-slate-500 hover:text-slate-850"
              }`}
            >
              Completed
            </button>
            <button
              onClick={() => setFilter("cancelled")}
              className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all duration-200 ${
                filter === "cancelled"
                  ? "bg-rose-600 text-white shadow-sm"
                  : "text-slate-500 hover:text-slate-850"
              }`}
            >
              Cancelled
            </button>
          </div>
        </div>

        {bookingHistory.length > 0 ? (
          <div className="space-y-6">
            {filteredBookings().map((booking) => {
              const isCurrentBooking = isCurrentBookingStatus(booking.status);
              
              const cabTypeObj = booking.cabType;
              const includedKm = cabTypeObj ? (Number(cabTypeObj.includedKm) || 0) : 0;
              const extraFarePerKm = cabTypeObj ? (Number(cabTypeObj.extraFarePerKm) || 0) : 0;
              const extraKm = booking.extraKm || 0;
              const extraKmCharge = booking.extraKmCharge || 0;
              const waitingTime = booking.waitingTime || 0;
              const waitingCharge = booking.waitingCharge || 0;
              
              const waitingChargePerMinute = (driver?.vehicleType?.waitingChargePerMinute !== undefined && driver?.vehicleType?.waitingChargePerMinute !== null)
                ? Number(driver.vehicleType.waitingChargePerMinute)
                : (cabTypeObj ? (Number(cabTypeObj.waitingChargePerMinute) || 0) : 5);

              const baseDue = booking.remainingAmount || (booking.totalAmount - (booking.advancePaid || 0));
              const totalCollectAmount = baseDue + extraKmCharge + waitingCharge;

              return (
                <div
                  key={booking._id}
                  className={`bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 ${
                    isCurrentBooking ? "ring-2 ring-blue-500/30 border-blue-200" : ""
                  }`}
                >
                  <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div>
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Booking ID</span>
                        <span className="text-xs font-black text-slate-750">{safeRender(booking.bookingId)}</span>
                      </div>
                      
                      <span
                        className={`px-2.5 py-1 rounded-md text-[9px] font-extrabold uppercase tracking-wider ${getStatusBadgeClass(booking.status)}`}
                      >
                        {booking.status === "inProgress" ? "On Journey" : booking.status}
                      </span>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-6 text-xs font-bold text-slate-600">
                      <div className="flex items-center gap-1.5">
                        <FaCalendarAlt className="text-slate-400 text-sm" />
                        <span>{formatDate(booking.pickupDate || booking.createdAt)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FaRupeeSign className="text-slate-400" />
                        <span className="text-sm font-black text-slate-800">
                          {booking.totalAmount ? `${booking.totalAmount.toFixed(2)}` : "N/A"}
                        </span>
                        <span className="text-[10px] text-slate-400 lowercase font-semibold">
                          ({booking.paymentMethod === "COD" ? "cash" : "online"})
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    {booking.status === "completed" && (
                      <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-4 space-y-3">
                        <EarningsBreakdown
                          booking={booking}
                          isExpanded={!!expandedBookings[booking._id]}
                          onToggle={() => toggleBookingExpand(booking._id)}
                          extraKm={extraKm}
                          includedKm={includedKm}
                          extraFarePerKm={extraFarePerKm}
                          extraKmCharge={extraKmCharge}
                          waitingTime={waitingTime}
                          waitingCharge={waitingCharge}
                          waitingChargePerMinute={waitingChargePerMinute}
                          baseDue={baseDue}
                          totalCollectAmount={totalCollectAmount}
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="relative pl-7 space-y-5 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
                        <div className="relative">
                          <span className="absolute -left-7 mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50 border border-emerald-100 text-[8px]">
                            🟢
                          </span>
                          <div>
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Pickup</span>
                            <h4 className="text-xs font-extrabold text-slate-800 mt-0.5">
                              {booking.pickupLocation?.name || booking.from || "Pickup Point"}
                            </h4>
                            <p className="text-[11px] text-slate-500 mt-0.5">{safeRender(booking.pickupAddress)}</p>
                          </div>
                        </div>

                        <div className="relative">
                          <span className="absolute -left-7 mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-50 border border-rose-100 text-[8px]">
                            🔴
                          </span>
                          <div>
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Dropoff</span>
                            <h4 className="text-xs font-extrabold text-slate-800 mt-0.5">
                              {booking.dropLocation?.name || booking.to || "Destination Point"}
                            </h4>
                            <p className="text-[11px] text-slate-500 mt-0.5">{safeRender(booking.dropAddress)}</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4 text-xs font-semibold text-slate-600">
                        <div className="flex gap-1.5 items-start">
                          <div className="w-7 h-7 rounded-lg bg-blue-50/50 flex items-center justify-center text-blue-500 flex-shrink-0">
                            <FaClock className="text-xs" />
                          </div>
                          <div>
                            <span className="text-[9px] text-slate-400 uppercase tracking-wider block">Scheduled</span>
                            <span className="text-slate-700 font-bold">{safeRender(booking.pickupTime)}</span>
                            {(booking.journeyType === "roundTrip" || booking.tripType === "roundTrip" || booking.returnDate) && booking.returnDate && (
                              <p className="text-[11px] text-indigo-600 font-bold mt-0.5">
                                Return: {formatDate(booking.returnDate)} {booking.returnTime && `@ ${booking.returnTime}`}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-1.5 items-start">
                          <div className="w-7 h-7 rounded-lg bg-indigo-50/50 flex items-center justify-center text-indigo-500 flex-shrink-0">
                            🚕
                          </div>
                          <div>
                            <span className="text-[9px] text-slate-400 uppercase tracking-wider block">Service Details</span>
                            <span className="text-slate-700 font-bold capitalize">
                              {booking.serviceType === "rental" ? "Local Rental" :
                               booking.serviceType === "airport" ? "Airport Transfer" :
                               booking.serviceType || "Outstation"}
                            </span>
                            {booking.viaCities && booking.viaCities.length > 0 && (
                              <p className="text-[11px] text-indigo-600 mt-0.5">
                                Via: {booking.viaCities.join(", ")}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-1.5 items-start">
                          <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0 text-[10px] font-black">
                            P
                          </div>
                          <div>
                            <span className="text-[9px] text-slate-400 uppercase tracking-wider block">Passenger Details</span>
                            <span className="text-slate-800 font-extrabold">{safeRender(booking.passengerDetails?.name || "Customer")}</span>
                            <p className="text-[11px] text-slate-500 mt-0.5">{safeRender(booking.passengerDetails?.phone)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {booking.completedAt && (
                      <div className="pt-4 border-t border-slate-100 space-y-3">
                        <div className="flex justify-between items-center text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                          <span>Completed on: {formatDateTime(booking.completedAt)}</span>
                          {booking.distance && <span>Distance: {safeRender(booking.distance)} km</span>}
                        </div>

                        {(() => {
                          const rating = safeGetRating(booking.userRating || booking.ratings?.user?.rating || booking.ratings?.user);
                          const comment = safeGetComment(booking.userRating || booking.ratings?.user?.comment || booking.ratings?.user || booking.userComment);

                          return rating > 0 ? (
                            <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4">
                              <div className="flex items-center justify-between flex-wrap gap-2">
                                <span className="text-xs font-black text-blue-800 uppercase tracking-wider">Passenger Review</span>
                                <div className="flex items-center gap-1.5">
                                  <div className="flex text-yellow-500 text-xs">
                                    {[...Array(5)].map((_, i) => (
                                      <span key={i} className={i < rating ? "text-yellow-500" : "text-slate-200"}>★</span>
                                    ))}
                                  </div>
                                  <span className="text-xs font-black text-slate-700">({rating}/5)</span>
                                </div>
                              </div>
                              {comment && (
                                <p className="text-xs text-slate-650 mt-2 italic bg-white/60 border border-slate-100 px-3 py-2 rounded-xl">
                                  "{comment}"
                                </p>
                              )}
                            </div>
                          ) : null;
                        })()}

                        {(() => {
                          const rating = safeGetRating(booking.driverRating || booking.ratings?.driver?.rating || booking.ratings?.driver);
                          const comment = safeGetComment(booking.driverRating || booking.ratings?.driver?.comment || booking.ratings?.driver);

                          return rating > 0 ? (
                            <div className="bg-amber-50/40 border border-amber-100/60 rounded-2xl p-4">
                              <div className="flex items-center justify-between flex-wrap gap-2">
                                <span className="text-xs font-black text-amber-800 uppercase tracking-wider">Your Review for Passenger</span>
                                <div className="flex items-center gap-1.5">
                                  <div className="flex text-yellow-500 text-xs">
                                    {[...Array(5)].map((_, i) => (
                                      <span key={i} className={i < rating ? "text-yellow-500" : "text-slate-200"}>★</span>
                                    ))}
                                  </div>
                                  <span className="text-xs font-black text-slate-700">({rating}/5)</span>
                                </div>
                              </div>
                              {comment && (
                                <p className="text-xs text-slate-650 mt-2 italic bg-white/60 border border-slate-100 px-3 py-2 rounded-xl">
                                  "{comment}"
                                </p>
                              )}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}

                    {isCurrentBooking && (
                      <div className="pt-4 border-t border-slate-100 flex justify-end">
                        <button
                          onClick={() => {
                            if (typeof window !== 'undefined') {
                              sessionStorage.setItem("selectedBookingId", booking._id);
                            }
                            navigate("/driver/current-ride");
                          }}
                          className="px-6 py-2.5 bg-blue-600 text-white font-extrabold text-xs rounded-xl hover:bg-blue-700 active:scale-95 shadow-sm shadow-blue-500/20 transition-all duration-200 uppercase tracking-wider"
                        >
                          {booking.status === "inProgress" ? "Resume Journey" : "Start Ride"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-slate-100 p-12 text-center max-w-xl mx-auto shadow-sm">
            <div className="w-20 h-20 mx-auto bg-slate-50 border border-slate-100 rounded-full flex items-center justify-center mb-6 text-4xl">
              📂
            </div>
            <h2 className="text-lg font-black text-slate-800 mb-2">No Ride History</h2>
            <p className="text-slate-500 text-sm leading-relaxed">
              You haven't completed any rides matching the selected filter yet.
            </p>
          </div>
        )}
      </div>
    </DriverLayout>
  );
};

export default RideHistory;
